import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useErrorToast } from "@/lib/hooks";
import { useAuth } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

export const ImportCard = () => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const showErr = useErrorToast();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const zipRef = useRef();
  const [photoRes, setPhotoRes] = useState(null);
  const uploadZip = async (file) => {
    setBusy(true);
    setPhotoRes(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/photos", fd);
      setPhotoRes(data);
      toast.success(t("imp.photosDone", data));
      qc.invalidateQueries();
    } catch (e) { showErr(e); } finally { setBusy(false); zipRef.current.value = ""; }
  };

  const template = async () => {
    try {
      const { data } = await api.get("/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      Object.assign(document.createElement("a"), { href: url, download: "rangeo-modele-import.xlsx" }).click();
      URL.revokeObjectURL(url);
    } catch (e) { showErr(e); }
  };
  const upload = async (file) => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import", fd);
      setResult(data);
      toast.success(t("imp.done", data));
      qc.invalidateQueries();
      refresh();
    } catch (e) { showErr(e); } finally { setBusy(false); ref.current.value = ""; }
  };

  return (
    <section data-testid="import-card" className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4">
      <h2 className="text-lg font-bold">{t("imp.title")}</h2>
      <p className="text-sm text-slate-500">{t("imp.desc")}</p>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="import-file-button" className="h-12 gap-2" disabled={busy} onClick={() => ref.current.click()}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{t("imp.choose")}
        </Button>
        <Button data-testid="import-template-button" variant="outline" className="h-12 gap-2" onClick={template}><FileSpreadsheet className="w-4 h-4" />{t("imp.template")}</Button>
      </div>
      <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" data-testid="import-file-input" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      <div className="border-t border-slate-100 pt-4 space-y-3">
        <p className="text-sm text-slate-500">{t("imp.photosDesc")}</p>
        <Button data-testid="import-photos-button" variant="outline" className="h-12 gap-2" disabled={busy} onClick={() => zipRef.current.click()}><Images className="w-4 h-4" />{t("imp.photos")}</Button>
        <input ref={zipRef} type="file" accept=".zip,application/zip" className="hidden" data-testid="import-photos-input" onChange={(e) => e.target.files[0] && uploadZip(e.target.files[0])} />
        {photoRes && (
          <div data-testid="import-photos-result" className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 space-y-1">
            <p>{t("imp.photosDone", photoRes)}</p>
            {photoRes.unmatched_count > 0 && <p className="text-amber-800 break-words">{t("imp.photosUnmatched", { count: photoRes.unmatched_count, files: photoRes.unmatched.join(", ") })}</p>}
          </div>
        )}
      </div>
      {result && (
        <div data-testid="import-result" className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 space-y-1">
          <p>{t("imp.done", result)}</p>
          {result.skipped > 0 && <p className="text-amber-800">{t("imp.skipped", result)}</p>}
        </div>
      )}
    </section>
  );
};
